import * as React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useRecovery } from '../hooks/useRecovery';
import { createRecoveryFlow, getRecoveryFlow } from '@/lib/api/kratos';
import type { RecoveryFlow } from '@/lib/api/kratos';

/**
 * RecoveryPage Component
 * 
 * Handles password recovery using Ory Kratos flows
 * Allows users to reset their password via email
 */
export function RecoveryPage() {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const [formValues, setFormValues] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [currentFlow, setCurrentFlow] = React.useState<RecoveryFlow | null>(null);
  
  const recoveryMutation = useRecovery();

  // Fetch or create recovery flow
  const { data: initialFlow, isLoading, error, refetch } = useQuery({
    queryKey: ['recoveryFlow', flowId],
    queryFn: async (): Promise<RecoveryFlow> => {
      if (flowId) {
        try {
          return await getRecoveryFlow(flowId);
        } catch (error) {
          console.warn('Failed to get existing flow, creating new one:', error);
          return await createRecoveryFlow();
        }
      }
      return await createRecoveryFlow();
    },
    retry: 1,
    staleTime: 0,
  });

  // Use the current flow (either initial or updated from mutation)
  const flow = currentFlow || initialFlow;

  // Update current flow when initial flow changes
  React.useEffect(() => {
    if (initialFlow && !currentFlow) {
      setCurrentFlow(initialFlow);
    }
  }, [initialFlow, currentFlow]);

  // Extract CSRF token from flow
  const csrfToken = React.useMemo(() => {
    if (!flow) return '';
    const csrfNode = flow.ui.nodes.find(node => 
      node.attributes.name === 'csrf_token'
    );
    return csrfNode?.attributes.value ? String(csrfNode.attributes.value) : '';
  }, [flow]);

  // Extract errors from flow and initialize form values
  React.useEffect(() => {
    if (!flow) return;

    console.log('Recovery flow state:', {
      flowId: flow.id,
      state: flow.state,
      nodes: flow.ui.nodes.map(n => ({ 
        type: n.type, 
        group: n.group, 
        name: n.attributes.name, 
        value: n.attributes.value 
      })),
      messages: flow.ui.messages,
    });

    const newErrors: Record<string, string> = {};
    const newFormValues: Record<string, string> = {};
    
    // Global flow errors
    if (flow.ui.messages) {
      flow.ui.messages.forEach(message => {
        if (message.type === 'error') {
          newErrors.general = message.text;
        }
      });
    }

    // Field-specific errors and initialize form values
    flow.ui.nodes.forEach(node => {
      if (node.messages && node.attributes.name) {
        node.messages.forEach(message => {
          if (message.type === 'error' && node.attributes.name) {
            newErrors[node.attributes.name] = message.text;
          }
        });
      }
      
      // Initialize form values from flow nodes
      if (node.attributes.name && node.attributes.value !== undefined) {
        newFormValues[node.attributes.name] = String(node.attributes.value);
      }
    });

    setErrors(newErrors);
    setFormValues(prev => ({ ...prev, ...newFormValues }));
  }, [flow?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!flow) return;

    try {
      const response = await recoveryMutation.mutateAsync({
        flowId: flow.id,
        values: {
          method: 'code', // Default method
          ...formValues,
          csrf_token: csrfToken,
        },
      });
      
      console.log('Recovery submission response:', response);
      
      // Check if recovery completed successfully
      if (response.state === 'passed_challenge' && !response.flow) {
        if (response.redirect_url) {
          console.log('Recovery code validated, redirecting to settings:', response.redirect_url);
          // Redirect to the settings page to complete password change
          window.location.href = response.redirect_url;
        } else {
          console.log('Recovery completed successfully, redirecting to login');
          // Recovery completed - redirect to login page
          window.location.href = '/auth/login?message=password_reset_success';
        }
        return;
      }
      
      // If the response contains an updated flow, use it directly
      if (response.flow) {
        console.log('Using updated flow from response');
        setCurrentFlow(response.flow);
        // Clear form values to prevent pre-population issues
        setFormValues({});
      } else {
        // Fallback to refetch if no flow in response
        console.log('No flow in response, refetching');
        refetch();
      }
    } catch (error) {
      // Error is handled by the mutation, but try to refetch
      console.error('Recovery submission error, refetching flow');
      refetch();
    }
  };

  const handleInputChange = (name: string, value: string) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load recovery form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => refetch()} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }



  // Determine the current step based on available nodes
  const hasEmailNode = flow?.ui.nodes.some(node => 
    node.attributes.name === 'email' && node.type === 'input'
  );
  const hasCodeNode = flow?.ui.nodes.some(node => 
    node.attributes.name === 'code' && node.type === 'input'
  );
  const hasPasswordNode = flow?.ui.nodes.some(node => 
    node.attributes.name === 'password' && node.type === 'input'
  );

  const getTitle = () => {
    if (hasPasswordNode) return 'Set New Password';
    if (hasCodeNode) return 'Enter Recovery Code';
    if (hasEmailNode) return 'Reset Password';
    return 'Reset Password';
  };

  const getDescription = () => {
    if (hasPasswordNode) return 'Choose a new password for your account.';
    if (hasCodeNode) return 'Enter the recovery code sent to your email.';
    if (hasEmailNode) return "Enter your email address and we'll send you a recovery code.";
    return "Enter your email address and we'll send you a recovery code.";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{getTitle()}</CardTitle>
          <CardDescription>{getDescription()}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global error message */}
          {errors.general && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {errors.general}
            </div>
          )}

          {/* Global info message */}
          {flow?.ui.messages && flow.ui.messages.some(m => m.type === 'info') && (
            <div className="space-y-2">
              {flow.ui.messages
                .filter(m => m.type === 'info')
                .map((message, index) => (
                  <div 
                    key={index}
                    className="p-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md"
                  >
                    {message.text}
                  </div>
                ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Render input nodes dynamically */}
            {flow?.ui.nodes
              .filter(node => 
                node.type === 'input' && 
                node.attributes.type !== 'hidden' && 
                node.attributes.type !== 'submit'
              )
              .map((node, index) => {
                const name = node.attributes.name as string;
                const type = node.attributes.type as string;
                const value = formValues[name] || '';
                
                return (
                  <div key={index} className="space-y-2">
                    <div className="text-sm font-medium">
                      {(node.meta?.label as any)?.text || name}
                    </div>
                    <Input
                      type={type}
                      name={name}
                      value={value}
                      onChange={(e) => handleInputChange(name, e.target.value)}
                      disabled={recoveryMutation.isPending}
                      placeholder={`Enter your ${name}`}
                      className={errors[name] ? 'border-destructive' : ''}
                      autoComplete="off"
                      required={node.attributes.required as boolean}
                    />
                    {errors[name] && (
                      <div className="text-sm text-destructive">{errors[name]}</div>
                    )}
                  </div>
                );
              })}

            {/* Render submit buttons */}
            {flow?.ui.nodes
              .filter(node => 
                node.type === 'input' && 
                node.attributes.type === 'submit'
              )
              .map((node, index) => {
                const isMainSubmit = node.attributes.name === 'method';
                const label = (node.meta?.label as any)?.text || 'Continue';
                
                return (
                  <Button 
                    key={index}
                    type="submit" 
                    variant={isMainSubmit ? 'default' : 'outline'}
                    className="w-full" 
                    disabled={recoveryMutation.isPending}
                    onClick={() => {
                      if (node.attributes.name && node.attributes.value) {
                        setFormValues(prev => ({
                          ...prev,
                          [node.attributes.name as string]: String(node.attributes.value)
                        }));
                      }
                    }}
                  >
                    {recoveryMutation.isPending ? 'Please wait...' : label}
                  </Button>
                );
              })}
          </form>

          {/* Link back to login */}
          <div className="text-center">
            <div className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link 
                to="/auth/login" 
                className="text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
